import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import {
  Search, Plus, Phone, Mail, Building2, MessageSquare,
  X, Clock, CheckCircle, XCircle, Sparkles,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { leadsApi } from '@/api/leads'
import type { ResendChannel } from '@/api/resendInvite'
import ResendInviteButtons from '@/components/ResendInviteButtons'
import type { Lead, LeadStatus, LeadSource } from '@/types'
import ConvertWizard from './ConvertWizard'

/* ─── Constants ──────────────────────────────────────────────── */

const STATUS_META: Record<LeadStatus, { label: string; color: string; bg: string; icon: typeof Clock }> = {
  new:        { label: 'New',        color: '#3b82f6', bg: '#3b82f620', icon: Sparkles },
  contacted:  { label: 'Contacted',  color: '#f59e0b', bg: '#f59e0b20', icon: Phone },
  qualified:  { label: 'Qualified',  color: '#8b5cf6', bg: '#8b5cf620', icon: CheckCircle },
  converted:  { label: 'Converted',  color: '#22c55e', bg: '#22c55e20', icon: Building2 },
  lost:       { label: 'Lost',       color: '#6b7280', bg: '#6b728020', icon: XCircle },
}

const SOURCE_LABELS: Record<LeadSource, string> = {
  website: 'Website (HE)',
  website_en: 'Website (EN)',
  referral: 'Referral',
  manual: 'Manual',
  partner: 'Partner',
  other: 'Other',
}

const PIPELINE_ORDER: LeadStatus[] = ['new', 'contacted', 'qualified', 'converted', 'lost']

/* ─── Lead Badge ─────────────────────────────────────────────── */

function LeadStatusBadge({ status }: { status: LeadStatus }) {
  const meta = STATUS_META[status] ?? STATUS_META.new
  return (
    <span
      className="px-2.5 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1"
      style={{ color: meta.color, background: meta.bg }}
    >
      <meta.icon size={12} />
      {meta.label}
    </span>
  )
}

/* ─── Add Lead Modal ──────────────────────────────────────────── */

function AddLeadModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', company_name: '', message: '', source: 'manual' as LeadSource })
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    if (!form.name.trim()) return
    setSaving(true)
    try {
      await leadsApi.create({
        name: form.name,
        email: form.email || null,
        phone: form.phone || null,
        company_name: form.company_name || null,
        notes: form.message || null,
        source: form.source,
        status: 'new',
      })
      onSaved()
      onClose()
    } catch {
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
      <div className="w-full max-w-md rounded-xl p-6" style={{ background: '#1a1d27', border: '1px solid #2d3148' }}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold text-white">Add Lead</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10" style={{ color: '#9ca3af' }}><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <input placeholder="Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500"
            style={{ background: '#252836', border: '1px solid #3f4258' }} />
          <input placeholder="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500"
            style={{ background: '#252836', border: '1px solid #3f4258' }} />
          <input placeholder="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500"
            style={{ background: '#252836', border: '1px solid #3f4258' }} />
          <input placeholder="Company" value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500"
            style={{ background: '#252836', border: '1px solid #3f4258' }} />
          <select value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value as LeadSource }))}
            className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500"
            style={{ background: '#252836', border: '1px solid #3f4258' }}>
            {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <textarea placeholder="Notes / message" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
            rows={3} className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
            style={{ background: '#252836', border: '1px solid #3f4258' }} />
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm" style={{ color: '#9ca3af' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.name.trim()}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
            style={{ background: '#6366f1' }}>
            {saving ? 'Saving…' : 'Add Lead'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ─── Lead Detail Modal ───────────────────────────────────────── */

function LeadDetailModal({ lead, onClose, onUpdated }: { lead: Lead; onClose: () => void; onUpdated: () => void }) {
  const [status, setStatus] = useState<LeadStatus>(lead.status)
  const [notes, setNotes] = useState(lead.notes ?? '')
  const [saving, setSaving] = useState(false)
  const [showWizard, setShowWizard] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    await supabase.from('jumpstart_leads')
      .update({ status, notes: notes || null, updated_at: new Date().toISOString() })
      .eq('id', lead.id)
    setSaving(false)
    onUpdated()
    onClose()
  }

  const age = Math.floor((Date.now() - new Date(lead.created_at).getTime()) / (1000 * 60 * 60 * 24))

  if (showWizard) {
    return <ConvertWizard lead={lead} onClose={() => setShowWizard(false)} onDone={onUpdated} />
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
      <div className="w-full max-w-lg rounded-xl p-6" style={{ background: '#1a1d27', border: '1px solid #2d3148' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">{lead.name}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10" style={{ color: '#9ca3af' }}><X size={18} /></button>
        </div>

        {/* Info grid */}
        <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
          {lead.email && (
            <div className="flex items-center gap-2" style={{ color: '#9ca3af' }}>
              <Mail size={14} /> {lead.email}
            </div>
          )}
          {lead.phone && (
            <div className="flex items-center gap-2" style={{ color: '#9ca3af' }}>
              <Phone size={14} /> {lead.phone}
            </div>
          )}
          {lead.company_name && (
            <div className="flex items-center gap-2" style={{ color: '#9ca3af' }}>
              <Building2 size={14} /> {lead.company_name}
            </div>
          )}
          <div className="flex items-center gap-2" style={{ color: '#9ca3af' }}>
            <Clock size={14} /> {age}d ago — {SOURCE_LABELS[lead.source] ?? lead.source}
          </div>
        </div>

        {lead.message && (
          <div className="rounded-lg p-3 mb-4 text-sm" style={{ background: '#252836', color: '#d1d5db' }}>
            <div className="flex items-center gap-1 mb-1 text-xs font-medium" style={{ color: '#9ca3af' }}>
              <MessageSquare size={12} /> Message
            </div>
            {lead.message}
          </div>
        )}

        {/* Status selector */}
        <div className="mb-4">
          <label className="text-xs font-medium mb-1.5 block" style={{ color: '#9ca3af' }}>Status</label>
          <div className="flex gap-1.5 flex-wrap">
            {PIPELINE_ORDER.map(s => (
              <button key={s} onClick={() => setStatus(s)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                style={{
                  background: status === s ? STATUS_META[s].bg : 'transparent',
                  color: status === s ? STATUS_META[s].color : '#6b7280',
                  border: `1px solid ${status === s ? STATUS_META[s].color + '40' : '#3f4258'}`,
                }}>
                {STATUS_META[s].label}
              </button>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className="mb-5">
          <label className="text-xs font-medium mb-1.5 block" style={{ color: '#9ca3af' }}>Internal Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
            className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
            style={{ background: '#252836', border: '1px solid #3f4258' }} />
        </div>

        {/* Converted org link */}
        {lead.converted_org && (
          <div className="rounded-lg p-3 mb-4 flex items-center gap-2 text-sm" style={{ background: '#22c55e10', border: '1px solid #22c55e30' }}>
            <CheckCircle size={16} style={{ color: '#22c55e' }} />
            <span style={{ color: '#22c55e' }}>Converted to org:</span>
            <span className="text-white font-medium">{lead.converted_org.name}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between">
          <div>
            {status !== 'converted' && !lead.converted_org && (
              <button onClick={() => setShowWizard(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white"
                style={{ background: '#22c55e' }}>
                <Building2 size={14} />
                Convert to Org
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm" style={{ color: '#9ca3af' }}>Cancel</button>
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
              style={{ background: '#6366f1' }}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ─── Main Page ───────────────────────────────────────────────── */

export default function LeadsPage() {
  const [leads, setLeads] = useState<Lead[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState<LeadStatus | 'all'>('all')
  const [showAdd, setShowAdd] = useState(false)
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
  const [stats, setStats] = useState<Record<LeadStatus, number>>({ new: 0, contacted: 0, qualified: 0, converted: 0, lost: 0 })

  const load = async () => {
    try {
      const all = await leadsApi.list()
      setLeads(all)
      const counts: Record<string, number> = { new: 0, contacted: 0, qualified: 0, converted: 0, lost: 0 }
      for (const l of all) counts[l.status] = (counts[l.status] || 0) + 1
      setStats(counts as Record<LeadStatus, number>)
    } catch {
    }
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const loadLeadTemplates = async () => {
    try {
      return await leadsApi.listLeadTemplates()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load templates')
      throw error
    }
  }

  const handleLeadResend = async (leadId: string, channel: ResendChannel, templateId: string) => {
    try {
      const { sent_to } = await leadsApi.resendLeadInvite(leadId, channel, templateId)
      toast.success(`${channel === 'email' ? 'Email' : 'WhatsApp'} sent to ${sent_to}`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to resend invite')
    }
  }

  const filtered = leads.filter(l => {
    if (filterStatus !== 'all' && l.status !== filterStatus) return false
    if (search) {
      const q = search.toLowerCase()
      return (
        l.name.toLowerCase().includes(q) ||
        (l.email ?? '').toLowerCase().includes(q) ||
        (l.company_name ?? '').toLowerCase().includes(q) ||
        (l.phone ?? '').toLowerCase().includes(q)
      )
    }
    return true
  })

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
    </div>
  )

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Leads</h1>
          <p className="text-sm mt-0.5" style={{ color: '#9ca3af' }}>
            {leads.length} total — {stats.new} new
          </p>
        </div>
        <button onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white self-start"
          style={{ background: '#6366f1' }}>
          <Plus size={16} /> Add Lead
        </button>
      </div>

      {/* Pipeline KPI strip */}
      <div className="grid grid-cols-5 gap-2">
        {PIPELINE_ORDER.map(s => {
          const meta = STATUS_META[s]
          const active = filterStatus === s
          return (
            <button key={s} onClick={() => setFilterStatus(active ? 'all' : s)}
              className="rounded-xl p-3 text-center transition-all"
              style={{
                background: active ? meta.bg : '#252836',
                border: `1px solid ${active ? meta.color + '40' : '#2d3148'}`,
              }}>
              <div className="text-2xl font-bold" style={{ color: active ? meta.color : 'white' }}>{stats[s]}</div>
              <div className="text-xs font-medium mt-0.5" style={{ color: active ? meta.color : '#9ca3af' }}>{meta.label}</div>
            </button>
          )
        })}
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#6b7280' }} />
        <input
          type="text" placeholder="Search leads…" value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9 pr-4 py-2 rounded-lg text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500 w-full sm:w-72"
          style={{ background: '#252836', border: '1px solid #3f4258' }}
        />
      </div>

      {/* Table */}
      <div className="rounded-xl overflow-hidden" style={{ background: '#252836', border: '1px solid #2d3148' }}>
        {/* Desktop */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid #2d3148' }}>
                {['Name', 'Contact', 'Company', 'Source', 'Status', 'Age', ''].map(h => (
                  <th key={h} className="text-left px-5 py-3 font-medium" style={{ color: '#9ca3af' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-8 text-center" style={{ color: '#6b7280' }}>
                  {search ? 'No leads match your search' : 'No leads yet — they\'ll appear here when someone fills the form'}
                </td></tr>
              ) : filtered.map((lead, i) => {
                const age = Math.floor((Date.now() - new Date(lead.created_at).getTime()) / (1000 * 60 * 60 * 24))
                return (
                  <tr key={lead.id}
                    className="cursor-pointer transition-colors hover:bg-white/5"
                    style={{ borderBottom: i < filtered.length - 1 ? '1px solid #2d3148' : undefined }}
                    onClick={() => setSelectedLead(lead)}>
                    <td className="px-5 py-3 text-white font-medium">{lead.name}</td>
                    <td className="px-5 py-3" style={{ color: '#9ca3af' }}>
                      <div className="flex flex-col gap-0.5 text-xs">
                        {lead.email && <span>{lead.email}</span>}
                        {lead.phone && <span>{lead.phone}</span>}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-sm" style={{ color: '#9ca3af' }}>{lead.company_name ?? '—'}</td>
                    <td className="px-5 py-3">
                      <span className="text-xs px-2 py-1 rounded-full" style={{ color: '#a5b4fc', background: '#6366f120' }}>
                        {SOURCE_LABELS[lead.source] ?? lead.source}
                      </span>
                    </td>
                    <td className="px-5 py-3"><LeadStatusBadge status={lead.status} /></td>
                    <td className="px-5 py-3 text-sm" style={{ color: '#9ca3af' }}>{age}d</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <ResendInviteButtons
                          hasEmail={!!lead.email}
                          hasPhone={!!lead.phone}
                          loadTemplates={loadLeadTemplates}
                          onResendWithTemplate={(channel, templateId) => handleLeadResend(lead.id, channel, templateId)}
                        />
                      <button className="text-xs px-3 py-1 rounded-lg" style={{ background: '#6366f120', color: '#a5b4fc' }}
                        onClick={e => { e.stopPropagation(); setSelectedLead(lead) }}>
                        View →
                      </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile */}
        <div className="md:hidden divide-y" style={{ borderColor: '#2d3148' }}>
          {filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm" style={{ color: '#6b7280' }}>No leads found</div>
          ) : filtered.map(lead => {
            const age = Math.floor((Date.now() - new Date(lead.created_at).getTime()) / (1000 * 60 * 60 * 24))
            return (
              <div key={lead.id}
                className="px-4 py-3 cursor-pointer hover:bg-white/5 transition-colors"
                onClick={() => setSelectedLead(lead)}>
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="min-w-0">
                    <div className="text-white font-semibold text-sm truncate">{lead.name}</div>
                    {lead.company_name && <div className="text-xs mt-0.5 truncate" style={{ color: '#9ca3af' }}>{lead.company_name}</div>}
                  </div>
                  <LeadStatusBadge status={lead.status} />
                </div>
                <div className="flex items-center gap-2 flex-wrap text-xs" style={{ color: '#9ca3af' }}>
                  <span className="px-2 py-0.5 rounded-full" style={{ color: '#a5b4fc', background: '#6366f120' }}>
                    {SOURCE_LABELS[lead.source] ?? lead.source}
                  </span>
                  <span>{age}d ago</span>
                  {lead.email && <span>{lead.email}</span>}
                </div>
                <div className="mt-3">
                  <ResendInviteButtons
                    hasEmail={!!lead.email}
                    hasPhone={!!lead.phone}
                    loadTemplates={loadLeadTemplates}
                    onResendWithTemplate={(channel, templateId) => handleLeadResend(lead.id, channel, templateId)}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Modals */}
      {showAdd && <AddLeadModal onClose={() => setShowAdd(false)} onSaved={load} />}
      {selectedLead && <LeadDetailModal lead={selectedLead} onClose={() => setSelectedLead(null)} onUpdated={load} />}
    </div>
  )
}
